import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Zap, MessageCircle, ChevronRight, Send } from "lucide-react";

const PRESETS = [
  { key: "default", icon: Zap, label: "Saluto ZAP", subtitle: "Il messaggio della tua Casa" },
  { key: "lunch", icon: MessageCircle, label: "Pranzo domenica?", text: "🍝 Pranzo domenica? Fammi sapere chi viene!" },
  { key: "call", icon: MessageCircle, label: "Chiamata stasera?", text: "📞 Ci sentiamo stasera? Un saluto!" },
  { key: "hug", icon: MessageCircle, label: "Un saluto", text: "❤️ Un grosso abbraccio dalla famiglia!" },
];

export default function ZapPicker({ open, onClose, family }) {
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState("");

  const handleClose = () => {
    setCustomMode(false);
    setCustomText("");
    onClose();
  };

  const sendZap = (text) => {
    if (!family) return;
    const url = `https://wa.me/${family.whatsapp_number}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    handleClose();
  };

  const defaultMessage = family?.whatsapp_message || "⚡ Ci vediamo presto!";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        data-testid="zap-picker-dialog"
        className="max-w-md rounded-3xl border-[#E8E1D9] bg-white"
      >
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full bg-[#FF7C43]/10 px-3 py-1">
            <Zap className="h-3 w-3 text-[#FF7C43]" fill="currentColor" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF7C43]">
              Manda uno ZAP
            </span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            {customMode ? "Scrivi il tuo ZAP" : "Cosa vuoi dire?"}
          </DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            {customMode
              ? "Componi il messaggio e invialo alla famiglia."
              : "Scegli un messaggio rapido o scrivine uno tuo."}
          </DialogDescription>
        </DialogHeader>

        {!customMode ? (
          <div className="space-y-2 py-2">
            {PRESETS.map((p, idx) => {
              const Icon = p.icon;
              const text = p.key === "default" ? defaultMessage : p.text;
              return (
                <button
                  key={p.key}
                  data-testid={`zap-preset-${p.key}`}
                  onClick={() => sendZap(text)}
                  className="group flex w-full items-center gap-3 rounded-2xl border border-[#E8E1D9] bg-white p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
                >
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${idx === 0 ? "papaya-gradient text-white" : "bg-[#FBF9F6] text-[#FF7C43]"}`}>
                    <Icon className="h-5 w-5" fill={idx === 0 ? "white" : "none"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#2D2926]">{p.label}</p>
                    <p className="truncate text-xs text-[#5C5855]">
                      {p.subtitle || text}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#C87941] opacity-0 transition group-hover:opacity-100" />
                </button>
              );
            })}

            <button
              data-testid="zap-preset-custom"
              onClick={() => setCustomMode(true)}
              className="group flex w-full items-center gap-3 rounded-2xl border-2 border-dashed border-[#E8E1D9] bg-white p-4 text-left transition-all duration-200 hover:border-[#C87941] hover:bg-[#FBF9F6]"
            >
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#C87941]/10 text-[#C87941]">
                <Send className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-[#2D2926]">Personalizzato</p>
                <p className="text-xs text-[#5C5855]">Scrivi il tuo messaggio</p>
              </div>
              <ChevronRight className="h-4 w-4 text-[#C87941] opacity-0 transition group-hover:opacity-100" />
            </button>
          </div>
        ) : (
          <div className="space-y-3 py-2">
            <Textarea
              data-testid="zap-custom-input"
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Scrivi qui il tuo ZAP..."
              rows={4}
              autoFocus
              className="rounded-xl border-[#E8E1D9] bg-white resize-none"
            />
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCustomMode(false)}
                className="rounded-full border-[#E8E1D9]"
              >
                Indietro
              </Button>
              <Button
                data-testid="zap-custom-send"
                type="button"
                onClick={() => customText.trim() && sendZap(customText.trim())}
                disabled={!customText.trim()}
                className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]"
              >
                <Zap className="mr-1 h-4 w-4" fill="white" />
                Invia ZAP
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
